import { FluentButton } from 'owa-fluent-v9-shims';
import classnames from 'owa-classnames';
import type { IIconProps } from '@fluentui/react/lib/Icon';
/* eslint-disable-next-line @fluentui/react-components/prefer-fluentui-v9 -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by using v9 components instead
 *	> Avoid using Icon from '@fluentui/react', as this package has already migrated to Fluent UI 9. Use a JSX SVG icon from '@fluentui/react-icons' instead. */
import { Icon } from '@fluentui/react/lib/Icon';
import React from 'react';
import { touchHandler } from 'owa-touch-handler';
import type { HoverActionIconProps, PropertyIconProps } from 'owa-mail-list-item-shared';
import { disabledHoverActionCursor } from 'owa-mail-listitem-styles/lib/scss/IconBar.scss';
import { makeStyles } from '@fluentui/react-components';
import { iconFilledClassName, iconRegularClassName } from '@fluentui/react-icons';
import { mergeStyles } from '@fluentui/merge-styles';
import { hoverIconInIconBar, full } from './IconBar.scss';

export interface IconBarProps {
    hoverActionIcons: HoverActionIconProps[];
    propertyIcons: PropertyIconProps[];
    iconClass: string;
    propertyIconClass: string;
    hoverIconClass: string;
}

const useStyles = makeStyles({
    icon: {
        [`& .${iconFilledClassName}`]: {
            display: 'none',
        },
        [`& .${iconRegularClassName}`]: {
            display: 'inline',
        },
        ':hover': {
            [`& .${iconFilledClassName}`]: {
                display: 'inline',
            },
            [`& .${iconRegularClassName}`]: {
                display: 'none',
            },
        },
    },
});

export const PropertyIcon: React.NamedExoticComponent<PropertyIconProps> = React.memo(
    function PropertyIcon({ iconClasses, iconName, id }: PropertyIconProps) {
        return <Icon id={id?.toString()} className={iconClasses} iconName={iconName} />;
    }
);

export const HoverIcon: React.NamedExoticComponent<HoverActionIconProps> = React.memo(
    function HoverIcon(props: HoverActionIconProps) {
        const iconStyles = useStyles();
        const iconProps: IIconProps = React.useMemo(() => {
            const iconClassName = mergeStyles(iconStyles.icon, props.iconClassName);

            return {
                iconName: props.iconName,
                className: classnames(
                    hoverIconInIconBar,
                    props.densityModeString === 'full' && full,
                    iconClassName
                ),
            };
        }, [props.iconName, props.iconClassName, props.densityModeString]);

        const buttonStyles = React.useMemo(
            () => ({
                root: classnames(
                    props.iconClasses,
                    props.iconName == 'None' && disabledHoverActionCursor
                ),
            }),
            [props.iconClasses]
        );
        const longPress = props.onContextMenu
            ? touchHandler({
                  onLongPress: props.onContextMenu,
              })
            : null;
        const onContextMenuHandler = React.useCallback(
            (evt: React.MouseEvent<unknown>) => {
                if (props.onContextMenu) {
                    props.onContextMenu(evt);
                }
            },
            [props.onContextMenu]
        );

        const onClickHandler = React.useCallback(
            (evt: React.MouseEvent<unknown>) => {
                evt.stopPropagation();
                props.onClickCommand?.();
            },
            [props.onClickCommand]
        );

        return (
            <FluentButton
                appearance="icon"
                tabIndex={-1}
                iconProps={iconProps}
                onClick={onClickHandler}
                onDoubleClick={onDoubleClickHandler}
                onRenderMenuIcon={onRenderMenuIconHandler}
                styles={buttonStyles}
                title={props.title}
                aria-label={props.title}
                menuProps={props.menuProps}
                onContextMenu={onContextMenuHandler}
                {...longPress}
            />
        );
    }
);

const onRenderMenuIconHandler = () => null;
const onDoubleClickHandler = (evt: React.MouseEvent<unknown>) => {
    evt.stopPropagation();
};
